import { FC, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, TrashSimple, CaretUp, CaretDown, BookOpenText } from '@phosphor-icons/react';
import { useEndpoints } from '../../../../hooks/useEndpoints';
import {
  IFieldWorkoutBlock,
  TacticalPhase,
  FieldBlockType,
} from '../../../../models/field-programming';
import styles from '../FieldWorkoutBuilderModal.module.scss';

interface Props {
  blocks: IFieldWorkoutBlock[];
  onChange: (b: IFieldWorkoutBlock[]) => void;
}

const PHASE_LABELS: Record<TacticalPhase, string> = {
  warmup: 'Riscaldamento',
  activation: 'Attivazione',
  athletic: 'Atletico',
  technical: 'Tecnica',
  situational: 'Situazionale',
  match: 'Partita',
  compensative: 'Compensativo',
  cool_down: 'Defaticamento',
};

const PHASE_COLORS: Record<TacticalPhase, string> = {
  warmup: '#f59e0b',
  activation: '#fb923c',
  athletic: '#ef4444',
  technical: '#8b5cf6',
  situational: '#6366f1',
  match: '#10b981',
  compensative: '#14b8a6',
  cool_down: '#0ea5e9',
};

const BLOCK_TYPE_LABELS: Record<FieldBlockType, string> = {
  sequential: 'Sequenziale',
  parallel: 'Parallelo',
  rotation: 'Rotazione',
  break: 'Pausa',
  physical: 'Fisico',
};

const BlocksStep: FC<Props> = ({ blocks, onChange }) => {
  const api = useEndpoints();
  const [showLibrary, setShowLibrary] = useState(false);

  const libQ = useQuery({
    queryKey: ['fieldProgramming', 'blocks', { limit: 100 }],
    queryFn: () =>
      api.fieldProgramming.blocks.list({ limit: 100 }).then((r) => r.data.docs),
  });

  const addFromLibrary = (id: string) => {
    const lib = libQ.data?.find((b) => b._id === id);
    if (!lib) return;
    onChange([
      ...blocks,
      {
        fieldBlockId: lib._id,
        snapshotName: lib.name,
        tacticalPhase: lib.tacticalPhase,
        blockType: lib.blockType,
        durationMinutes: lib.defaultDurationMinutes,
        exercises: lib.exercises,
        order: blocks.length,
      },
    ]);
    setShowLibrary(false);
  };

  const addInline = () =>
    onChange([
      ...blocks,
      {
        snapshotName: 'Nuovo blocco',
        tacticalPhase: 'warmup' as TacticalPhase,
        blockType: 'sequential' as FieldBlockType,
        exercises: [],
        order: blocks.length,
      },
    ]);

  const updateBlock = (i: number, patch: Partial<IFieldWorkoutBlock>) =>
    onChange(blocks.map((b, idx) => (idx === i ? { ...b, ...patch } : b)));

  const removeBlock = (i: number) =>
    onChange(blocks.filter((_, idx) => idx !== i).map((b, idx) => ({ ...b, order: idx })));

  const moveBlock = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= blocks.length) return;
    const arr = [...blocks];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    onChange(arr.map((b, idx) => ({ ...b, order: idx })));
  };

  return (
    <div className={styles.section}>
      <div className={styles.actionRow}>
        <button
          type="button"
          className={styles.secondaryAction}
          onClick={() => setShowLibrary((v) => !v)}
        >
          <BookOpenText size={14} weight="bold" />
          {showLibrary ? 'Nascondi libreria' : 'Da libreria'}
        </button>
        <button type="button" className={styles.secondaryAction} onClick={addInline}>
          <Plus size={14} weight="bold" />
          Inline rapido
        </button>
      </div>

      {showLibrary && (
        <div className={styles.libraryPanel}>
          {libQ.isLoading && <p className={styles.libraryHint}>Caricamento libreria...</p>}
          {libQ.data?.length === 0 && (
            <p className={styles.libraryHint}>Libreria vuota.</p>
          )}
          {libQ.data?.map((b) => (
            <button
              key={b._id}
              type="button"
              className={styles.libraryItem}
              onClick={() => addFromLibrary(b._id)}
            >
              <span className={styles.libraryItemName}>{b.name}</span>
              <span className={styles.libraryItemMeta}>
                {PHASE_LABELS[b.tacticalPhase]} · {b.exercises.length} esercizi
              </span>
            </button>
          ))}
        </div>
      )}

      {blocks.length === 0 && (
        <div className={styles.emptyHint}>
          Nessun blocco. Inizia da libreria oppure aggiungi inline rapido.
        </div>
      )}

      <div className={styles.exercisesList}>
        {blocks.map((b, i) => {
          const phaseColor = PHASE_COLORS[b.tacticalPhase];
          return (
            <div key={i} className={styles.exerciseCard}>
              <div className={styles.blockColorStripInline} style={{ background: phaseColor }} />
              <div className={styles.blockTopRow}>
                <span className={styles.exerciseOrder}>{i + 1}</span>
                <input
                  className={styles.input}
                  placeholder="Nome blocco"
                  value={b.snapshotName}
                  onChange={(e) => updateBlock(i, { snapshotName: e.target.value })}
                />
                <button
                  type="button"
                  className={styles.iconBtn}
                  onClick={() => moveBlock(i, -1)}
                  disabled={i === 0}
                  title="Sposta su"
                >
                  <CaretUp size={14} weight="bold" />
                </button>
                <button
                  type="button"
                  className={styles.iconBtn}
                  onClick={() => moveBlock(i, 1)}
                  disabled={i === blocks.length - 1}
                  title="Sposta giù"
                >
                  <CaretDown size={14} weight="bold" />
                </button>
                <button
                  type="button"
                  className={styles.deleteExerciseBtn}
                  onClick={() => removeBlock(i)}
                  title="Rimuovi"
                >
                  <TrashSimple size={15} />
                </button>
              </div>

              <div className={styles.exerciseParamsRow}>
                <div className={styles.paramField}>
                  <label className={styles.paramLabel}>Fase</label>
                  <select
                    className={styles.paramSelect}
                    value={b.tacticalPhase}
                    onChange={(e) =>
                      updateBlock(i, { tacticalPhase: e.target.value as TacticalPhase })
                    }
                  >
                    {(Object.keys(PHASE_LABELS) as TacticalPhase[]).map((p) => (
                      <option key={p} value={p}>
                        {PHASE_LABELS[p]}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={styles.paramField}>
                  <label className={styles.paramLabel}>Tipo</label>
                  <select
                    className={styles.paramSelect}
                    value={b.blockType}
                    onChange={(e) =>
                      updateBlock(i, { blockType: e.target.value as FieldBlockType })
                    }
                  >
                    {(Object.keys(BLOCK_TYPE_LABELS) as FieldBlockType[]).map((t) => (
                      <option key={t} value={t}>
                        {BLOCK_TYPE_LABELS[t]}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={styles.paramField}>
                  <label className={styles.paramLabel}>Durata (min)</label>
                  <input
                    type="number"
                    min={0}
                    className={styles.paramInput}
                    placeholder="—"
                    value={b.durationMinutes ?? ''}
                    onChange={(e) =>
                      updateBlock(i, {
                        durationMinutes: e.target.value ? Number(e.target.value) : undefined,
                      })
                    }
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BlocksStep;
